import styled from '@emotion/styled';

import { COLORS, SANS_SERIF_FONT_FAMILY, mq } from '@clutter/clean';

import { SERIF_FONT_FAMILY } from '@root/constants/fonts';

import { ResponsiveImage } from '@shared/responsive_image';

export const HowItWorksSection = styled.section`
  padding: 80px 24px;
  background: ${COLORS.cloud};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80vh;

  ${mq({
    padding: ['0px 20px', '60px 24px', '0px 32px'],
    minHeight: ['auto', 'auto', '80vh'],
    height: ['634px', 'auto', 'auto'],
  })}
`;

export const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  max-width: 1050px;
  width: 100%;

  ${mq({
    display: ['flex', 'flex', 'grid'],
    flexDirection: ['column', 'column', 'row'],
    gridTemplateColumns: ['1fr', '1fr', '1fr 1fr'],
    gap: ['24px', '40px', '60px'],
    padding: ['0', '0', '0'],
  })}
`;

export const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 337px;

  ${mq({
    order: ['2', '2', '0'],
    display: ['none', 'none', 'flex'],
  })}
`;

export const LeftSectionMobile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  ${mq({
    display: ['flex', 'flex', 'none'],
    order: ['2', '2', '0'],
    gap: ['12px', '16px', '0'],
  })}
`;

export const LaptopContainer = styled.div`
  position: relative;
  width: 473px;
  height: 337px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  ${mq({
    width: ['409.441px', '350px', '473px'],
    height: ['272.961px', '250px', '337px'],
    aspectRatio: ['409.44/272.96', 'auto', 'auto'],
    borderRadius: ['var(--L, 36px)', '0', '0'],
    maxWidth: ['100%', '100%', '473px'],
  })}
`;

export const LaptopImage = styled(ResponsiveImage)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1));

  ${mq({
    width: ['409.441px', '100%', '100%'],
    height: ['272.961px', '100%', '100%'],
    aspectRatio: ['409.44/272.96', 'auto', 'auto'],
    borderRadius: ['var(--L, 36px)', '0', '0'],
    filter: [
      'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.08))',
      'drop-shadow(0 15px 30px rgba(0, 0, 0, 0.09))',
      'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.1))',
    ],
  })}
`;

export const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  height: 314px;

  ${mq({
    gap: ['20px', '24px', '32px'],
    height: ['auto', 'auto', '314px'],
    width: ['100%', '100%', 'auto'],
  })}
`;

export const MainHeading = styled.h2`
  font-family: 'Recoleta', serif;
  font-size: 45px;
  font-weight: 500;
  color: ${COLORS.panther};
  margin: 0 0 40px 0;
  line-height: 1.4;
  letter-spacing: 0px;

  ${mq({
    fontSize: ['40px', '36px', '45px'],
    marginBottom: ['16px', '24px', '0px'],
    order: ['1', '1', '0'],
    textAlign: ['left', 'left', 'left'],
    lineHeight: ['1.4', '1.3', '1.4'],
    alignSelf: ['stretch', 'auto', 'auto'],
  })}
`;

export const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 120px;
  transition: opacity 0.3s ease;

  ${mq({
    order: ['4', '4', '0'],
    textAlign: ['left', 'center', 'left'],
    minHeight: ['200px', '130px', '120px'],
    gap: ['4px', '8px', '16px'],
    width: ['327px', 'auto', 'auto'],
  })}
`;

export const StepTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: ${COLORS.tealDark};
  margin: 0;
  line-height: 1.3;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: flex-start;

  ${mq({
    fontSize: ['28px', '20px', '24px'],
    lineHeight: ['1.4', '1.25', '1.3'],
    fontWeight: ['500', '600', '600'],
    color: ['#000', COLORS.tealDark, COLORS.tealDark],
    fontFamily: [SERIF_FONT_FAMILY, 'inherit', 'inherit'],
    alignSelf: ['stretch', 'auto', 'auto'],
    fontFeatureSettings: ['"liga" off, "clig" off', 'normal', 'normal'],
    height: ['auto', '50px', 'auto'],
    marginBottom: ['0', '0', '0'],
  })}
`;

export const StepDescription = styled.div`
  font-size: 16px;
  color: ${COLORS.panther};
  margin: 0;
  line-height: 1.5;
  font-weight: 400;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: flex-start;

  ${mq({
    fontSize: ['18px', '15px', '16px'],
    lineHeight: ['1.4', '1.45', '1.5'],
    maxWidth: ['100%', '100%', 'none'],
    alignSelf: ['stretch', 'auto', 'auto'],
    color: ['#000', COLORS.panther, COLORS.panther],
    fontFamily: [SANS_SERIF_FONT_FAMILY, 'inherit', 'inherit'],
    height: ['120px', '80px', 'auto'],
  })}

  ul {
    list-style-type: disc;
    margin: 12px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    font-weight: 700;
  }
`;

export const DesktopProgressBar = styled.div`
  ${mq({
    display: ['none', 'none', 'block'],
    order: ['0', '0', '0'],
  })}
`;

export const MobileProgressBar = styled.div`
  width: 100%;

  ${mq({
    display: ['block', 'block', 'none'],
  })}

  > div {
    margin-bottom: 0 !important;
  }
`;
